import { useState, useEffect, useCallback } from 'react';
import { api } from '@/App';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { toast } from 'sonner';
import { Building2, Plus, Star, MapPin, Phone, Mail } from 'lucide-react';
import { EnterpriseToolbar, EnterpriseTableHead, StatusBadge, EmptyState, SelectCheckbox, RowActions } from '@/components/EnterpriseTable';

export default function VendorsPage() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [vendorDialog, setVendorDialog] = useState(false);
  const [viewDialog, setViewDialog] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [vendorForm, setVendorForm] = useState({
    name: '', contact_person: '', email: '', phone: '', address: '',
    city: '', state: '', pincode: '', gst_number: '', pan_number: '',
    payment_terms: 30, categories: [], rating: 0
  });

  const fetchVendors = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/vendors');
      setVendors(res.data);
    } catch (err) {
      toast.error('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchVendors(); }, [fetchVendors]);

  const handleSaveVendor = async () => {
    try {
      if (selectedVendor) {
        await api.put(`/vendors/${selectedVendor.id}`, vendorForm);
        toast.success('Vendor updated successfully');
      } else {
        await api.post('/vendors', vendorForm);
        toast.success('Vendor created successfully');
      }
      setVendorDialog(false);
      resetForm();
      fetchVendors();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save vendor');
    }
  };

  const handleDeleteVendor = async (id) => {
    if (!window.confirm('Are you sure you want to delete this vendor?')) return;
    try {
      await api.delete(`/vendors/${id}`);
      toast.success('Vendor deleted');
      fetchVendors();
    } catch (err) {
      toast.error('Failed to delete vendor');
    }
  };

  const resetForm = () => {
    setSelectedVendor(null);
    setVendorForm({
      name: '', contact_person: '', email: '', phone: '', address: '',
      city: '', state: '', pincode: '', gst_number: '', pan_number: '',
      payment_terms: 30, categories: [], rating: 0
    });
  };

  const openEditDialog = (vendor) => {
    setSelectedVendor(vendor);
    setVendorForm({
      name: vendor.name || '',
      contact_person: vendor.contact_person || '',
      email: vendor.email || '',
      phone: vendor.phone || '',
      address: vendor.address || '',
      city: vendor.city || '',
      state: vendor.state || '',
      pincode: vendor.pincode || '',
      gst_number: vendor.gst_number || '',
      pan_number: vendor.pan_number || '',
      payment_terms: vendor.payment_terms || 30,
      categories: vendor.categories || [],
      rating: vendor.rating || 0
    });
    setVendorDialog(true);
  };

  const filteredVendors = vendors.filter(v => 
    v.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.contact_person?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.gst_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.city?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredVendors.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredVendors.map(v => v.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const handleViewSelected = () => {
    const vendor = vendors.find(v => v.id === selectedIds[0]);
    if (vendor) {
      setSelectedVendor(vendor);
      setViewDialog(true);
    }
  };

  const handleEditSelected = () => {
    const vendor = vendors.find(v => v.id === selectedIds[0]);
    if (vendor) openEditDialog(vendor);
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star key={i} className={`h-3 w-3 ${i < rating ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} />
    ));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="h-12 w-12 rounded-full border-4 border-emerald-500 border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-slate-50" data-testid="vendors-page">
      {/* Enterprise Toolbar */}
      <EnterpriseToolbar
        title="Vendors / Suppliers"
        totalCount={filteredVendors.length}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onRefresh={fetchVendors}
        onAdd={() => { resetForm(); setVendorDialog(true); }}
        onEdit={handleEditSelected}
        onView={handleViewSelected}
        onPrint={() => window.print()}
        onExport={() => toast.info('Export functionality coming soon')}
        onDelete={() => {
          selectedIds.forEach(id => handleDeleteVendor(id));
          setSelectedIds([]);
        }}
        selectedCount={selectedIds.length}
        addLabel="New Vendor"
      />

      {/* Data Table */}
      <div className="flex-1 overflow-auto">
        <Card className="border-slate-200 shadow-sm rounded-none border-x-0">
          <CardContent className="p-0">
            <Table>
              <thead>
                <tr className="bg-slate-100 border-b">
                  <EnterpriseTableHead className="w-12">
                    <SelectCheckbox 
                      checked={selectedIds.length === filteredVendors.length && filteredVendors.length > 0}
                      indeterminate={selectedIds.length > 0 && selectedIds.length < filteredVendors.length}
                      onCheckedChange={toggleSelectAll}
                    />
                  </EnterpriseTableHead>
                  <EnterpriseTableHead sortable filterable className="w-16">S.No</EnterpriseTableHead>
                  <EnterpriseTableHead sortable filterable>Vendor Code</EnterpriseTableHead>
                  <EnterpriseTableHead sortable filterable>Vendor Name</EnterpriseTableHead>
                  <EnterpriseTableHead sortable filterable>Contact Person</EnterpriseTableHead>
                  <EnterpriseTableHead sortable filterable>Phone</EnterpriseTableHead>
                  <EnterpriseTableHead sortable filterable>City</EnterpriseTableHead>
                  <EnterpriseTableHead sortable filterable>GST Number</EnterpriseTableHead>
                  <EnterpriseTableHead sortable filterable>Payment Terms</EnterpriseTableHead>
                  <EnterpriseTableHead sortable>Rating</EnterpriseTableHead>
                  <EnterpriseTableHead sortable filterable>Status</EnterpriseTableHead>
                  <EnterpriseTableHead className="w-24">Actions</EnterpriseTableHead>
                </tr>
              </thead>
              <tbody>
                {filteredVendors.map((vendor, index) => (
                  <TableRow 
                    key={vendor.id} 
                    className={`hover:bg-slate-50 ${selectedIds.includes(vendor.id) ? 'bg-blue-50' : ''}`}
                  >
                    <TableCell className="py-1 px-1.5">
                      <SelectCheckbox 
                        checked={selectedIds.includes(vendor.id)}
                        onCheckedChange={() => toggleSelect(vendor.id)}
                      />
                    </TableCell>
                    <TableCell className="font-mono text-xs text-slate-500 py-1 px-1.5">{index + 1}</TableCell>
                    <TableCell className="font-mono text-xs text-slate-600 py-1 px-1.5">{vendor.code || '-'}</TableCell>
                    <TableCell className="font-medium text-xs text-slate-800 py-1 px-1.5">{vendor.name}</TableCell>
                    <TableCell className="text-xs text-slate-600 py-1 px-1.5">{vendor.contact_person || '-'}</TableCell>
                    <TableCell className="text-xs text-slate-600 py-1 px-1.5">{vendor.phone || '-'}</TableCell>
                    <TableCell className="text-xs text-slate-600 py-1 px-1.5">{vendor.city || '-'}</TableCell>
                    <TableCell className="font-mono text-xs text-slate-600 py-1 px-1.5">{vendor.gst_number || '-'}</TableCell>
                    <TableCell className="text-xs text-slate-600 py-1 px-1.5">{vendor.payment_terms || 30} days</TableCell>
                    <TableCell className="py-1 px-1.5">
                      <div className="flex">{renderStars(vendor.rating || 0)}</div>
                    </TableCell>
                    <TableCell className="py-1 px-1.5">
                      <StatusBadge status={vendor.is_active ? 'active' : 'inactive'} />
                    </TableCell>
                    <TableCell className="py-1 px-1.5">
                      <RowActions
                        onView={() => { setSelectedVendor(vendor); setViewDialog(true); }}
                        onEdit={() => openEditDialog(vendor)}
                        onDelete={() => handleDeleteVendor(vendor.id)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
            
            {filteredVendors.length === 0 && (
              <EmptyState
                icon={Building2}
                title="No Vendors Found"
                description={searchTerm ? 'Try adjusting your search criteria' : 'Add your first vendor to get started'}
                action={
                  <Button onClick={() => { resetForm(); setVendorDialog(true); }} className="bg-emerald-600 hover:bg-emerald-700">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Vendor
                  </Button>
                }
              />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Vendor Dialog */}
      <Dialog open={vendorDialog} onOpenChange={setVendorDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold">{selectedVendor ? 'Edit Vendor' : 'New Vendor'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-6 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
              <div className="col-span-2 grid gap-2">
                <Label className="text-xs font-medium text-slate-500 uppercase">Vendor Name *</Label>
                <Input 
                  value={vendorForm.name} 
                  onChange={(e) => setVendorForm({...vendorForm, name: e.target.value})}
                  placeholder="Enter vendor/company name"
                  className="bg-white"
                />
              </div>
              <div className="grid gap-2">
                <Label className="text-xs font-medium text-slate-500 uppercase">Contact Person</Label>
                <Input 
                  value={vendorForm.contact_person}
                  onChange={(e) => setVendorForm({...vendorForm, contact_person: e.target.value})}
                  placeholder="Primary contact name"
                  className="bg-white"
                />
              </div>
              <div className="grid gap-2">
                <Label className="text-xs font-medium text-slate-500 uppercase">Phone</Label>
                <Input 
                  value={vendorForm.phone}
                  onChange={(e) => setVendorForm({...vendorForm, phone: e.target.value})}
                  placeholder="+91 98765 43210"
                  className="bg-white"
                />
              </div>
              <div className="grid gap-2">
                <Label className="text-xs font-medium text-slate-500 uppercase">Email</Label>
                <Input 
                  type="email"
                  value={vendorForm.email}
                  onChange={(e) => setVendorForm({...vendorForm, email: e.target.value})}
                  placeholder="vendor@company.com"
                  className="bg-white"
                />
              </div>
              <div className="grid gap-2">
                <Label className="text-xs font-medium text-slate-500 uppercase">Payment Terms (Days)</Label>
                <Select value={String(vendorForm.payment_terms)} onValueChange={(v) => setVendorForm({...vendorForm, payment_terms: Number(v)})}>
                  <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Immediate</SelectItem>
                    <SelectItem value="7">7 Days</SelectItem>
                    <SelectItem value="15">15 Days</SelectItem>
                    <SelectItem value="30">30 Days</SelectItem>
                    <SelectItem value="45">45 Days</SelectItem>
                    <SelectItem value="60">60 Days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Address */}
            <div className="grid gap-4 p-4 bg-slate-50 rounded-lg">
              <Label className="text-sm font-semibold text-slate-700">Address Information</Label>
              <div className="grid gap-2">
                <Label className="text-xs font-medium text-slate-500 uppercase">Street Address</Label>
                <Textarea 
                  value={vendorForm.address}
                  onChange={(e) => setVendorForm({...vendorForm, address: e.target.value})}
                  placeholder="Full street address"
                  className="bg-white min-h-[60px]"
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="grid gap-2">
                  <Label className="text-xs font-medium text-slate-500 uppercase">City</Label>
                  <Input 
                    value={vendorForm.city}
                    onChange={(e) => setVendorForm({...vendorForm, city: e.target.value})}
                    placeholder="City"
                    className="bg-white"
                  />
                </div>
                <div className="grid gap-2">
                  <Label className="text-xs font-medium text-slate-500 uppercase">State</Label>
                  <Input 
                    value={vendorForm.state}
                    onChange={(e) => setVendorForm({...vendorForm, state: e.target.value})}
                    placeholder="State"
                    className="bg-white"
                  />
                </div>
                <div className="grid gap-2">
                  <Label className="text-xs font-medium text-slate-500 uppercase">PIN Code</Label>
                  <Input 
                    value={vendorForm.pincode}
                    onChange={(e) => setVendorForm({...vendorForm, pincode: e.target.value})}
                    placeholder="400001"
                    className="bg-white"
                  />
                </div>
              </div>
            </div>

            {/* Tax Info */}
            <div className="grid grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg">
              <div className="grid gap-2">
                <Label className="text-xs font-medium text-slate-500 uppercase">GST Number</Label>
                <Input 
                  value={vendorForm.gst_number}
                  onChange={(e) => setVendorForm({...vendorForm, gst_number: e.target.value})}
                  placeholder="22AAAAA0000A1Z5"
                  className="bg-white font-mono"
                />
              </div>
              <div className="grid gap-2">
                <Label className="text-xs font-medium text-slate-500 uppercase">PAN Number</Label>
                <Input 
                  value={vendorForm.pan_number}
                  onChange={(e) => setVendorForm({...vendorForm, pan_number: e.target.value})}
                  placeholder="AAAAA0000A"
                  className="bg-white font-mono"
                />
              </div>
              <div className="grid gap-2">
                <Label className="text-xs font-medium text-slate-500 uppercase">Vendor Rating</Label>
                <Select value={String(vendorForm.rating)} onValueChange={(v) => setVendorForm({...vendorForm, rating: Number(v)})}>
                  <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Not Rated</SelectItem>
                    <SelectItem value="1">⭐ 1 Star</SelectItem>
                    <SelectItem value="2">⭐⭐ 2 Stars</SelectItem>
                    <SelectItem value="3">⭐⭐⭐ 3 Stars</SelectItem>
                    <SelectItem value="4">⭐⭐⭐⭐ 4 Stars</SelectItem>
                    <SelectItem value="5">⭐⭐⭐⭐⭐ 5 Stars</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setVendorDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveVendor} className="bg-emerald-600 hover:bg-emerald-700">
              {selectedVendor ? 'Update Vendor' : 'Create Vendor'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Vendor Dialog */}
      <Dialog open={viewDialog} onOpenChange={setViewDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="text-xl font-semibold">{selectedVendor?.name}</DialogTitle>
              <StatusBadge status={selectedVendor?.is_active ? 'active' : 'inactive'} />
            </div>
          </DialogHeader>
          {selectedVendor && (
            <div className="space-y-6">
              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <Phone className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Phone</p>
                    <p className="font-medium">{selectedVendor.phone || 'Not provided'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center">
                    <Mail className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Email</p>
                    <p className="font-medium">{selectedVendor.email || 'Not provided'}</p>
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <MapPin className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Address</p>
                  <p className="font-medium">
                    {selectedVendor.address || ''}<br />
                    {[selectedVendor.city, selectedVendor.state, selectedVendor.pincode].filter(Boolean).join(', ') || 'Not provided'}
                  </p>
                </div>
              </div>

              {/* Tax & Payment Info */}
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500 mb-1">GST Number</p>
                  <p className="font-mono font-medium">{selectedVendor.gst_number || '-'}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500 mb-1">Payment Terms</p>
                  <p className="font-medium">{selectedVendor.payment_terms || 30} days</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500 mb-1">Rating</p>
                  <div className="flex">{renderStars(selectedVendor.rating || 0)}</div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setViewDialog(false)}>Close</Button>
            <Button onClick={() => { setViewDialog(false); openEditDialog(selectedVendor); }} className="bg-emerald-600 hover:bg-emerald-700">
              Edit Vendor
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
